export type Permission = { resource: string; action: string };

/** Elevated capabilities assigned to super_admin (also granted when manage_users is present). */
const SUPER_ADMIN_CAPABILITIES: Permission[] = [
  { resource: "users", action: "manage_users" },
  { resource: "newsletter", action: "send" },
  { resource: "settings", action: "manage_site" },
  { resource: "navigation", action: "manage_structure" },
  { resource: "pages", action: "manage" },
];

function isSuperAdminUser(permissions: Permission[]): boolean {
  return permissions.some(
    (p) => p.resource === "users" && p.action === "manage_users",
  );
}

function permissionMatches(
  permission: Permission,
  resource: string,
  action: string,
): boolean {
  return (
    (permission.resource === "*" || permission.resource === resource) &&
    (permission.action === "*" || permission.action === action)
  );
}

export function hasPermission(
  permissions: Permission[],
  resource: string,
  action: string,
): boolean {
  if (permissions.some((p) => permissionMatches(p, resource, action))) {
    return true;
  }

  if (isSuperAdminUser(permissions)) {
    return SUPER_ADMIN_CAPABILITIES.some(
      (p) => p.resource === resource && p.action === action,
    );
  }

  return false;
}

export function canManageUsers(permissions: Permission[]): boolean {
  return hasPermission(permissions, "users", "manage_users");
}

export function canSendNewsletter(permissions: Permission[]): boolean {
  return hasPermission(permissions, "newsletter", "send");
}

export function canManageSiteConfig(permissions: Permission[]): boolean {
  return hasPermission(permissions, "settings", "manage_site");
}

export function canManageMenuStructure(permissions: Permission[]): boolean {
  return hasPermission(permissions, "navigation", "manage_structure");
}

export function canManageCraftPages(permissions: Permission[]): boolean {
  return hasPermission(permissions, "pages", "manage");
}

export function canWrite(permissions: Permission[], resource: string): boolean {
  return hasPermission(permissions, resource, "write") || hasPermission(permissions, "*", "write");
}

export function canRead(permissions: Permission[], resource: string): boolean {
  return hasPermission(permissions, resource, "read") || hasPermission(permissions, "*", "read");
}

export function canDelete(permissions: Permission[], resource: string): boolean {
  return hasPermission(permissions, resource, "delete") || hasPermission(permissions, "*", "delete");
}
